import { QueryLazyOptions } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { between, down, up } from 'styled-breakpoints';
import styled from 'styled-components';
import * as yup from 'yup';

import Button from '@rebound/design-system/components/Button';
import { InputFieldSearch } from '@rebound/design-system/components/FormHelpers';
import { colorSchemeConfig } from '@rebound/design-system/theme/colorScheme';
import { UserJourneyEnum } from '@rebound/shared-models/retailer/retailer.model';
import { Return } from '@rebound/shared-models/return/return.model';

import { useRetailerJourney } from '@modules/retailer/retailer.context';
import { storeOrderParamsAndResolveRedirect } from '@modules/return-journey/return-journey.utils';

import { TrackReturnFormType } from '../tracking.model';

type TrackReturnFormProps = {
  findReturnByTracking: (
    options?: QueryLazyOptions<Record<string, any>> | undefined,
  ) => void;
  trackReturnData?: Return[];
  scheme: string;
};

const TrackReturnFormValuesSchema = yup.object().shape({
  trackingOrNumber: yup.string().required(),
});

const TrackReturnForm = ({
  findReturnByTracking,
  trackReturnData,
  scheme,
}: TrackReturnFormProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const userJourney = useRetailerJourney();
  const {
    handleSubmit,
    control,
    formState: { errors: formErrors },
    setValue,
  } = useForm<TrackReturnFormType>({
    resolver: yupResolver(TrackReturnFormValuesSchema),
  });
  const [redirectUrl, setRedirectUrl] = useState('');

  const queryParams: any = {
    orderReference: router.query.orderReference as string,
    trackingNumber: router.query.trackingNumber as string,
  };

  const trackReturnHandleSubmit = useCallback(
    (values: TrackReturnFormType) => {
      findReturnByTracking({
        variables: { trackingOrNumber: values.trackingOrNumber },
      });
    },
    [findReturnByTracking],
  );

  useEffect(() => {
    if (queryParams.orderReference || queryParams.trackingNumber) {
      const trackingOrNumber = queryParams.orderReference
        ? queryParams.orderReference
        : queryParams.trackingNumber;
      setValue('trackingOrNumber', trackingOrNumber);
      trackReturnHandleSubmit({ trackingOrNumber });
    }
  }, [
    queryParams.orderReference,
    queryParams.trackingNumber,
    setValue,
    trackReturnHandleSubmit,
  ]);

  useEffect(() => {
    if (userJourney === UserJourneyEnum.EMAIL_VERIFICATION) {
      const token = localStorage.getItem('token');
      setRedirectUrl(token ? '/dashboard/orders' : '/');
    } else {
      setRedirectUrl('/search-order');
    }
  }, [userJourney]);

  return (
    <Form onSubmit={handleSubmit(trackReturnHandleSubmit)}>
      <Controller
        name="trackingOrNumber"
        control={control}
        defaultValue=""
        render={({ field }) => (
          <InputFieldItem
            name={field.name}
            label={t('trackReturnPage.trackingInputLabel')}
            inputProps={{
              ...field,
              'data-cy': 'track-return-input',
            }}
            error={
              formErrors.trackingOrNumber &&
              formErrors.trackingOrNumber.type === 'required'
                ? t('errors.trackReturnNumberValidation')
                : trackReturnData?.length === 0
                  ? t('trackReturnPage.returnWasNotFound')
                  : null
            }
          />
        )}
      />
      <ButtonLink href={redirectUrl} passHref>
        <BackHomeButton
          title={t('orderLookup.backToHomePage')}
          scheme={scheme}
          onClick={() =>
            storeOrderParamsAndResolveRedirect(userJourney, trackReturnData)
          }
        ></BackHomeButton>
      </ButtonLink>
    </Form>
  );
};

const ButtonLink = styled(Link)`
  ${down('sm')} {
    max-width: 375px;
    width: 100%;
  }
`;

const Form = styled.form`
  display: flex;

  ${down('sm')} {
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 500px;
    margin: auto;
  }
`;

const InputFieldItem = styled(InputFieldSearch)`
  width: 100%;
  max-width: 375px;
`;

const BackHomeButton = styled(Button)<{ scheme: string }>`
  button {
    background-color: ${(props) => colorSchemeConfig[props.scheme].v600};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    font-weight: 400;
  }

  ${down('sm')} {
    max-width: 375px;
    text-align: center;
    width: 100%;
  }

  ${up('sm')} {
    button {
      padding: 6px 10px;
      margin: 21px 0 0 10px;
    }
  }

  ${between('xs', 'sm')} {
    button {
      font-size: 15px;
      line-height: 18px;
    }
  }
`;

export default TrackReturnForm;
